/* eslint-disable import/no-cycle */
import React from "react";

import moment from "moment";

import { Dialog } from "primereact";
import ReactPaginate from "react-paginate";
import { Spinner } from "react-bootstrap";
import toastUtils from "../../../../../utils/ToastUtils";

import BackButtom from "../../../../components/backButton";
import DataGrid, { ItemField } from "../../../../components/datagrid";
import ChooseModal from "../../../../components/modals/choose-modal";
import Icons from "../../../../components/icons-helper";
import ButtonRound from "../../../../components/button-action";
import { DocumentCard } from "../../../../components/cards";

import { FilterModel } from "../../../../../domain/models/enum";
import {
  DocumentsModel,
  DocumentsResponseModel,
} from "../../../../../domain/models/documents";

import { Cards, CardWrapper, Wrapper, ContainerButtonActions } from "./styles";
import { theme } from "../../../../styles/theme";
import { PaginateStyled } from "../../../../components/datagrid/styled";

import { documentType } from "../../../../../utils";
import { showErrorMessage, handleClickInfo } from "../../../../helpers";
import { settings } from "../../../../../main/configs/settings";
import { Link } from "../../../../components/cards/navigate-card/styles";
import Icon from "../../../../components/icon";
import TemplateInvoice from "../../../../components/template-invoice";
import { makeRemoteInvoice } from "../../../../../main/factories/usecases/invoice/remote-invoice-factory";
import { makeRemoteDocument } from "../../../../../main/factories/usecases/documents/remote-documents-factory";
import { Filters } from "./filters";

type PageProps = {
  list: DocumentsResponseModel | undefined;
  setParams: any;
  loading?: boolean;
  handleReload: () => void;
  isSuperAdmin?: boolean;
};

export const formCurrency = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
});

const InvoiceListPage: React.FC<PageProps> = ({
  list,
  setParams,
  loading,
  handleReload,
  isSuperAdmin,
}) => {
  const [selected, setSelected] = React.useState<DocumentsModel | undefined>();
  const [remove, setRemove] = React.useState<DocumentsModel | undefined>();

  const [shouldForcePage, setShouldForcePage] = React.useState<boolean>(false);

  // O invoice vindo de /documents-emission traz `developerCompany` como wrapper
  // (DeveloperCompanyModel → o template precisa do `.company`), `currency` como
  // objeto e `serviceValue` ausente (só `total`). Normalização idêntica à de
  // `documents/list` (setSelectedInvoiceData) para o "Bill from", moeda e valor
  // aparecerem como na tela interna e no outro modal de visualização.
  const buildTemplateInvoice = (invoice: any): any => {
    if (!invoice) return invoice;

    return {
      ...invoice,
      serviceValue: String(invoice?.total),
      developerCompany: (invoice?.developerCompany as any)?.company,
      currency: `${invoice?.currency?.name} - (${invoice?.currency?.code}) - ${invoice?.currency?.symbol} - ${invoice?.currency?.id}`,
    };
  };

  const exportDocument = async (data: DocumentsModel): Promise<void> => {
    try {
      if (data?.invoice?.id) {
        await makeRemoteInvoice().export(data.invoice.id);
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail ||
        error?.response?.data?.messageClient ||
        error?.message ||
        "Erro ao exportar invoice.";
      toastUtils.showMessage({ type: "error", message: msg });
    }
  };

  // Exclui o documento emitido (document-emission); o backend remove a invoice
  // vinculada em cascata. Deletar a invoice direto violaria a FK do
  // document_emission que a referencia.
  const handleDelete = async (id: number): Promise<void> => {
    try {
      await makeRemoteDocument().delete(id);
      toastUtils.showMessage({
        type: "success",
        message: "Invoice excluído com sucesso!",
      });
      handleReload();
    } catch (error: any) {
      showErrorMessage(
        error?.userMessage || error?.detail || error?.message || "Erro ao excluir invoice.",
      );
    } finally {
      setRemove(undefined);
    }
  };

  const fields: ItemField[] = [
    {
      title: "Número",
      key: "id",
      size: 80,
      renderItem: (data: DocumentsModel) => data?.invoice?.id || "-",
    },
    {
      title: "Data",
      key: "issuedOn",
      size: 110,
      renderItem: (data: DocumentsModel) =>
        moment(data?.issuedOn).format("DD/MM/YYYY"),
    },
    ...(isSuperAdmin
      ? [
          {
            title: "Prestador",
            key: "prestador",
            renderItem: (data: DocumentsModel) => {
              const name =
                (data?.invoice?.developerCompany as any)?.company
                  ?.socialReason || "-";
              return (
                <span
                  title={name}
                  style={{
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 180,
                  }}
                >
                  {name}
                </span>
              );
            },
          },
        ]
      : []),
    {
      title: "Tomador",
      key: "companyName",
      renderItem: (data: DocumentsModel) => {
        const name = data?.invoice?.companyName || "-";
        return (
          <span
            title={name}
            style={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 180,
            }}
          >
            {name}
          </span>
        );
      },
    },
    {
      title: "Valor",
      key: "total",
      renderItem: (data: DocumentsModel) =>
        formCurrency.format(Number(data?.invoice?.total || 0)),
    },
    {
      title: "Ações",
      key: "actions",
      renderItem: (data: DocumentsModel) => (
        <ContainerButtonActions>
          <ButtonRound
            backgroundColor={theme.colors.darkBlue}
            title="Visualizar"
            onClick={() => setSelected(data)}
            color={theme.colors.green}
          >
            <Icons height={25} width={25} name="eye" color={theme.colors.green} />
          </ButtonRound>
          <ButtonRound
            title="Download"
            backgroundColor={theme.colors.darkBlue}
            onClick={() => exportDocument(data)}
            color={theme.colors.lightBlue}
          >
            <Icons
              height={20}
              width={20}
              name="download"
              color={theme.colors.primary}
            />
          </ButtonRound>
          <ButtonRound
            backgroundColor={theme.colors.darkBlue}
            onClick={() => setRemove(data)}
            title="Excluir"
            color={theme.colors.red}
          >
            <Icons height={15} width={15} name="close" color={theme.colors.red} />
          </ButtonRound>
        </ContainerButtonActions>
      ),
    },
  ];

  const handlePage = (e: any): void => {
    setParams((prev: FilterModel) => ({
      ...prev,
      page: e,
    }));

    setShouldForcePage(false);
  };

  return (
    <Wrapper>
      <BackButtom text="Invoice" />
      <CardWrapper>
        <Filters onSubmit={(e) => setParams(e)} />
        <Link to="/invoice/create" invertColor={false} style={{ maxWidth: 240 }}>
          Novo Invoice <Icon name="arrow-right" />
        </Link>
      </CardWrapper>
      <Cards>
        {loading && (
          <div className="d-flex justify-content-center">
            <Spinner animation="grow" variant="primary" />
          </div>
        )}

        {!loading &&
          list?.content.map((item: any) => (
            <DocumentCard
              key={item.id}
              actions={{
                detail: () => handleClickInfo(item),
                view: () => setSelected(item),
                download: () => exportDocument(item),
                cancel: () => setRemove(item),
              }}
              docType={documentType.find((el) => el.ENUM === item.type)?.label}
              number={item?.invoice?.id}
              date={moment(item?.issuedOn).format("DD/MM/YYYY")}
              taker={item?.invoice?.companyName}
              value={formCurrency.format(Number(item?.invoice?.total || 0)) as any}
            />
          ))}

        <PaginateStyled>
          <ReactPaginate
            breakLabel="..."
            nextLabel=">"
            pageRangeDisplayed={settings.perPage}
            previousLabel="<"
            nextClassName="next-button"
            prevClassName="prev-button"
            pageCount={list?.totalPages || 0}
            forcePage={shouldForcePage ? 0 : undefined}
            onPageChange={(e: any) => handlePage(e.selected)}
          />
        </PaginateStyled>
      </Cards>

      <DataGrid
        loading={!!loading}
        fields={fields}
        data={list?.content || []}
        totalPages={list?.totalPages || 0}
        changePage={handlePage}
        shouldForcePage={shouldForcePage}
      />

      <ChooseModal
        text="Você deseja realmente excluir o invoice?"
        onConfirm={() => remove?.id && handleDelete(remove.id)}
        onCancel={() => setRemove(undefined)}
        isVisible={!!remove}
        toggleVisibility={() => setRemove(undefined)}
      />

      <Dialog
        header="Invoice:"
        visible={!!selected}
        style={{ width: window.innerWidth < 479 ? "90vw" : "50vw" }}
        headerStyle={{
          background: theme.colors.darkBlue,
          color: theme.colors.white,
        }}
        contentStyle={{
          background: theme.colors.darkBlue,
          color: theme.colors.white,
        }}
        footer={null}
        onHide={() => setSelected(undefined)}
      >
        <TemplateInvoice
          invoice={buildTemplateInvoice(selected?.invoice)}
          onCancelCallback={() => setSelected(undefined)}
        />
      </Dialog>
    </Wrapper>
  );
};
export default InvoiceListPage;
